import CommonCss from "lib/common/style.module.scss";

import Css from "./style.module.scss";

import {
  ButtonRadioGroup,
  DropBoxPicker,
  FileDropZone,
  Form,
  GoogleDrivePicker,
  ModalWindow
} from "lib/common";
import { FiFileText } from "react-icons/fi";
import {
  FormCheckbox,
  FormGroup
} from "shards-react";
import { checkDocumentsFetching } from "selectors/documents";
import { getSelectedBusinessData } from "selectors/businesses";
import { getTextsData } from "selectors/texts";
import { useSelector } from "react-redux";
import { v4 as uuid } from "uuid";
import Constants from "const/Constants";
import DocumentFileTypes from "const/DocumentFileTypes";
import DocumentPaymentTypes from "const/DocumentPaymentTypes";
import DocumentTypes from "const/DocumentTypes";
import EditDocumentWindowFooter from "./lib/EditDocumentWindowFooter";
import Platforms from "utils/Platforms";
import React, { useCallback, useMemo, useState } from "react";
import Texts from "utils/Texts";
import classNames from "classnames";

const FILE_DROP_FORMATS = Object.keys(DocumentFileTypes);

const FILE_DROP_ALLOWED_TYPE = Object.values(DocumentFileTypes).reduce(
  (aggregator, { extensions, mimeType }) => ([...aggregator, ...extensions, mimeType]), []
);

const AddDocumentWindow = ({ onClose }) => {
  const fetchingData = useSelector(checkDocumentsFetching);

  const { messages, uiTexts } = useSelector(getTextsData);

  const selectedBusinessData = useSelector(getSelectedBusinessData);

  const [type, setType] = useState("");

  const [paymentType, setPaymentType] = useState("");

  const [files, setFiles] = useState(null);

  const [merge, setMerge] = useState(false);

  const uniqueId = useMemo(() => uuid(), []);

  const footerProps = useMemo(() => {
    return {
      okButtonText: files?.length
        ? Texts.replaceVars(
          merge ? uiTexts.mergeCountDocuments : uiTexts.uploadCountDocuments,
          { count: files.length }
        )
        : (merge ? uiTexts.mergeDocuments : uiTexts.uploadDocuments),
      disabledButtons: fetchingData,
      disabledOkButton: !files || (merge && files.length === 1)
    };
  }, [fetchingData, files, merge, uiTexts]);

  const typeOptions = useMemo(() => {
    return [
      { value: "", label: uiTexts.auto },
      { value: DocumentTypes.INVOICE, label: uiTexts.invoice },
      { value: DocumentTypes.BILL, label: uiTexts.bill },
      { value: DocumentTypes.RECEIPT, label: uiTexts.receipt },
      { value: DocumentTypes.CREDIT_NOTE, label: uiTexts.creditNote }
    ];
  }, [uiTexts]);

  const closeWindow = useCallback(async(result) => {
    const documentResult = { merge, files, type, paymentType };

    await onClose(...(result ? [documentResult, result.toReport, result.toExclude] : []));
  }, [merge, paymentType, type, files, onClose]);

  const handleClose = useCallback((result) => {
    closeWindow(result);
  }, [closeWindow]);

  const handleFormSubmit = useCallback((event) => {
    event.preventDefault();
  }, []);

  const handleTypeTabChange = useCallback((value) => {
    setType(value === DocumentTypes.BILL ? DocumentTypes.INVOICE : value);
    setPaymentType(
      (value === DocumentTypes.INVOICE || value === DocumentTypes.SALES_RECEIPT) ? DocumentPaymentTypes.SELL
        : (
          (value === DocumentTypes.BILL || value === DocumentTypes.RECEIPT) ? DocumentPaymentTypes.BUY : ""
        )
    );
  }, []);

  const handleFileDropZoneDrop = useCallback((value, fileRejections) => {
    if (value.length) setFiles(value);
    else if (fileRejections.length) setFiles(null);
  }, []);

  const handleGoogleDrivePickerFilesPicked = useCallback((value, oAuthToken) => {
    onClose({
      cloudService: "gdrive",
      oAuthToken,
      googleDocs: value,
      type,
      paymentType
    });
  }, [onClose, paymentType, type]);

  const handleDropBoxPickerFilesPicked = useCallback((value) => {
    onClose({
      cloudService: "dropbox",
      filesLinks: value,
      type,
      paymentType
    });
  }, [onClose, paymentType, type]);

  const handleMergeCheckboxChange = useCallback(() => {
    setMerge((prev) => !prev);
  }, []);

  return (
    <ModalWindow
      fullScreen
      key={uniqueId}
      className={Css.modalWindow}
      bodyClassName={Css.modalBody}
      modalClassName={classNames(
        Css.editDocumentWindowContainer,
        "mode-default",
        uniqueId
      )}
      config={{
        confirm: true,
        headerText: uiTexts.uploadDocuments
      }}
      iconComponent={FiFileText}
      headerWrapperProps={{ className: classNames(Css.modalHeaderWrapper, CommonCss.zIndexA) }}
      footerComponent={EditDocumentWindowFooter}
      footerProps={footerProps}
      footerWrapperProps={{ className: classNames(Css.modalFooterWrapper, CommonCss.zIndexA) }}
      onClose={handleClose}>
      <Form className={Css.form} onSubmit={handleFormSubmit}>
        <FormGroup row className={CommonCss.flexCenter}>
          <ButtonRadioGroup
            outline
            size="sm"
            theme="light"
            initialValue={type}
            options={typeOptions}
            onChange={handleTypeTabChange} />
        </FormGroup>
        <FormGroup row>
          <FileDropZone
            extensions={FILE_DROP_FORMATS}
            accept={FILE_DROP_ALLOWED_TYPE}
            hint={Texts.replaceVars(messages.documentUploadPageLimit, { pages: Constants.MAX_DOCUMENT_PAGES })}
            onDrop={handleFileDropZoneDrop} />
        </FormGroup>
        {!Platforms.checkIsTouchDevice() && <>
          <FormGroup row className={CommonCss.flexCenter} >
            <div>{messages.uploadFromCloudStorage}:</div>
          </FormGroup>
          <FormGroup row className={CommonCss.flexCenter} >
            <div className={Css.cloudDocumentsPickers}>
              <GoogleDrivePicker onFilesPicked={handleGoogleDrivePickerFilesPicked} />
              <DropBoxPicker onFilesPicked={handleDropBoxPickerFilesPicked} />
            </div>
          </FormGroup>
        </>}
        {selectedBusinessData.meta.emailAddress && (
          <FormGroup row className={classNames(Css.autoUploadEmailGroup, CommonCss.flexCenter)} >
            <div>{messages.autoUploadDocuments}</div>
            <div><b>{selectedBusinessData.meta.emailAddress}</b></div>
          </FormGroup>
        )}
        <FormGroup row className={classNames(CommonCss.flexCenter, Css.merge)}>
          <FormCheckbox
            toggle
            checked={merge}
            onChange={handleMergeCheckboxChange} />
          <span>{messages.mergeDocumentsCheckbox}</span>
        </FormGroup>
      </Form>
    </ModalWindow>
  );
};

export default React.memo(AddDocumentWindow);
